import React, { useState } from 'react';
import { useAchievements } from '../contexts/AchievementContext';
import shareUtils from '../utils/shareUtils';
import apiService from '../services/api';
import '../styles/ShareModal.css';

const ShareModal = ({ isOpen, onClose, recipe }) => {
  const [copied, setCopied] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const { handleApiResponse } = useAchievements();

  if (!isOpen || !recipe) return null;

  const currentUrl = shareUtils.getCurrentUrl();
  const shareTitle = recipe.title;
  const shareText = `Check out this amazing recipe: ${recipe.title}`;

  // Track recipe sharing for achievements
  const trackShare = async (platform) => {
    try {
      const response = await apiService.trackRecipeShare(recipe.id, { platform });
      handleApiResponse(response);
    } catch (error) {
      console.error('Failed to track share:', error);
    }
  };

  const handleShare = async (shareFunction, ...args) => {
    try {
      const result = await shareFunction(...args);
      if (result && result.message) {
        setShareMessage(result.message);
        setTimeout(() => setShareMessage(''), 3000);
      }
    } catch (error) {
      setShareMessage('Failed to share. Please try again.');
      setTimeout(() => setShareMessage(''), 3000);
    }
  };

  const handleCopyLink = async () => {
    const result = await shareUtils.copyToClipboard(currentUrl, shareTitle);
    
    // Track the share
    await trackShare('copy_link');
    
    setCopied(true);
    setShareMessage(result.message);
    setTimeout(() => {
      setCopied(false);
      setShareMessage('');
    }, 3000);
  };

  const handleNativeShare = async () => {
    const result = await shareUtils.shareNative(currentUrl, shareTitle, shareText);
    
    // Track the share if successful
    if (result.success) {
      await trackShare('native_share');
    }
    
    setShareMessage(result.message);
    setTimeout(() => setShareMessage(''), 3000);
  };

  const shareOptions = [
    {
      id: 'facebook',
      name: 'Facebook',
      icon: '📘',
      color: '#1877f2',
      action: () => handleShare(shareUtils.shareToFacebook, currentUrl, shareTitle)
    },
    {
      id: 'twitter',
      name: 'Twitter',
      icon: '🐦',
      color: '#1da1f2',
      action: () => handleShare(shareUtils.shareToTwitter, currentUrl, shareTitle)
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp',
      icon: '💬',
      color: '#25d366',
      action: () => handleShare(shareUtils.shareToWhatsApp, currentUrl, shareTitle)
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: '📷',
      color: '#e4405f',
      action: () => {
        const result = shareUtils.shareToInstagram(currentUrl, shareTitle);
        setShareMessage(result.message);
        setTimeout(() => setShareMessage(''), 5000);
      }
    },
    {
      id: 'pinterest',
      name: 'Pinterest',
      icon: '📌',
      color: '#bd081c',
      action: () => handleShare(shareUtils.shareToPinterest, currentUrl, shareTitle, recipe.imageUrl)
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: '💼',
      color: '#0077b5',
      action: () => handleShare(shareUtils.shareToLinkedIn, currentUrl, shareTitle)
    },
    {
      id: 'email',
      name: 'Email',
      icon: '📧',
      color: '#ea4335',
      action: () => handleShare(shareUtils.shareViaEmail, currentUrl, shareTitle)
    },
    {
      id: 'sms',
      name: 'SMS',
      icon: '📱',
      color: '#34a853',
      action: () => handleShare(shareUtils.shareViaSMS, currentUrl, shareTitle)
    }
  ];

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="share-modal-header">
          <h2>Share Recipe</h2>
          <button className="share-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="share-modal-body">
          <div className="recipe-share-preview">
            {recipe.imageUrl && (
              <img src={recipe.imageUrl} alt={recipe.title} className="share-preview-image" />
            )}
            <div className="share-preview-content">
              <h3>{recipe.title}</h3>
              <p>{recipe.description}</p>
            </div>
          </div>

          {/* Native Share Button (if supported) */}
          {navigator.share && (
            <div className="native-share-section">
              <button 
                className="native-share-btn"
                onClick={handleNativeShare}
              >
                <span className="share-icon">📤</span>
                Share with System
              </button>
            </div>
          )}

          {/* Copy Link Button */}
          <div className="copy-link-section">
            <button 
              className={`copy-link-btn ${copied ? 'copied' : ''}`}
              onClick={handleCopyLink}
            >
              <span className="share-icon">{copied ? '✅' : '🔗'}</span>
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>

          {/* Social Media Options */}
          <div className="share-options-grid">
            {shareOptions.map((option) => (
              <button
                key={option.id}
                className="share-option-btn"
                onClick={async () => {
                  option.action();
                  await trackShare(option.id);
                }}
                style={{ '--option-color': option.color }}
              >
                <span className="share-option-icon">{option.icon}</span>
                <span className="share-option-name">{option.name}</span>
              </button>
            ))}
          </div>

          {/* Share Message */}
          {shareMessage && (
            <div className="share-message">
              <span className="share-message-icon">ℹ️</span>
              <span className="share-message-text">{shareMessage}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
